import { CACHE_MAP_KEY } from './index';

/**
 * A factory function to create a conditional hook. The condition must return a boolean value
 * that does not change after initialisation. This function will cache the result of the condition
 * and use it to determine which hook to call. If the condition changes between renders,
 * unexpected behaviour may occur.
 *
 * The hook generated by this factory will also force the condition to be effectively constant
 * by caching the result of the condition on the first call. In non-production environments,
 * it will throw an error if the condition changes between renders to help catch potential issues.
 *
 * If for some reason you need to reset the cache (i.e. for tests where you change the condition between runs),
 * you can call `DO_NOT_USE_THIS_IN_PRODUCTION_EVER_resetConditionalHooksFactoryCache()`.
 *
 * **This hook should only be used for Feature Gates & Experiments where the value doesn't change.**
 * ```ts
 * // Acceptable use
 * const useMyHook = conditionalHooksFactory(
 *   () => isFeatureEnabled('my-feature'),
 *   useMyNewHook,
 *   useMyOldHook,
 * );
 *
 * // Not acceptable use
 * const useMyHook = conditionalHooksFactory(
 *    () => someVarThatCouldChange === 'my-value',
 *    useMyNewHook,
 *    useMyOldHook,
 * );
 * ```
 *
 * @param condition The condition function that will be used to determine which hook to call.
 * @param newHook The new hook to call if the condition is true.
 * @param oldHook The old hook to call if the condition is false.
 * @returns A migrator hook that will call either the new hook or the old hook based on the condition.
 */
export function conditionalHooksFactory<Result, Args extends unknown[]>(
	condition: () => boolean,
	newHook: (...args: Args) => Result,
	oldHook: (...args: Args) => Result,
): (...args: Args) => Result {
	const hookFn = (...args: Args) => {
		// call each time so we can track exposures
		const conditionResult = condition();
		const cache: WeakMap<(...args: never[]) => unknown, boolean> = globalThis[CACHE_MAP_KEY];
		if (!cache.has(hookFn)) {
			cache.set(hookFn, conditionResult);
		}

		// Extra level of safety for dev environment to notify devs of changed condition
		if (process.env.NODE_ENV !== 'production') {
			if (cache.get(hookFn) !== conditionResult) {
				throw new Error(
					'Conditional hook called with different condition, this breaks the rules of hooks!',
				);
			}
		}

		if (cache.get(hookFn)) {
			return newHook(...args);
		} else {
			return oldHook(...args);
		}
	};
	return hookFn;
}
